import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { Filters, NodeSn, Security, TableEndpoint } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import type { ApiTableModel } from 'components/ApiTable/model/ApiTableModel';
import { useApiTable } from 'components/ApiTable/model/useApiTable';
import { useCloudConstructExplorer } from 'components/ApiTablePage/useCloudConstructExplorer';
import {
  type TableDeviceViewHook,
  useTableDeviceView,
} from 'components/ApiTablePage/useTableDeviceView';
import { useTableQueryOptions } from 'components/ApiTablePage/useTableQueryOptions';
import { useTableSharedView } from 'components/ApiTablePage/useTableSharedView';
import type { ConnectedApiTableProps } from 'components/ConnectedApiTable/ConnectedApiTable';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTableQueryTmpSettingsOption } from './useTableQueryTmpSettingsOption';

export type ApiTablePageHookProps = {
  endpoint: TableEndpoint<any>;
  additionalApiTableProps?: ConnectedApiTableProps;
  /** Applied only when the page was not opened with `options` filters in the URL. */
  initialFilters?: Filters;
};

export type ApiTablePageHook = {
  tableDeviceView: TableDeviceViewHook;
  apiTableModel: ApiTableModel;
  sharedViewLoading: boolean;
};

export const useApiTablePage = (
  props: ApiTablePageHookProps,
): ApiTablePageHook => {
  const tableDeviceView = useTableDeviceView(props.endpoint);
  useCloudConstructExplorer(tableDeviceView.deviceView);
  const queryOptions = useTableQueryOptions();
  const queryTmpSettingsOption = useTableQueryTmpSettingsOption();
  const navigate = useNavigate();
  const location = useLocation();
  const { contentAfterContainer } = useGlobalStateContextSelectors({
    contentAfterContainer: prop('contentAfterContainer'),
  });

  const handleSecurityClick = useStableCallback(
    (
      hostSn: NodeSn,
      name: string,
      securityType: Security.SecurityType,
      highlightedRuleIndex?: number,
    ) => {
      navigate(location.pathname, {
        state: {
          ruleDetailModal: {
            name,
            highlightedRuleIndex,
            hostSn,
            securityType,
          },
        },
      });
    },
  );

  const tableCellContext: Partial<TableCellContext> = useMemo(
    () => ({
      onSecurityClick: handleSecurityClick,
      ...tableDeviceView.tableCellContext,
      ...props.additionalApiTableProps?.tableCellContext,
    }),
    [
      tableDeviceView.tableCellContext,
      props.additionalApiTableProps?.tableCellContext,
    ],
  );

  const apiTableModel = useApiTable({
    ...props.additionalApiTableProps,
    endpoint: props.endpoint,
    tableCellContext,
    initialFilters: queryOptions?.filters ?? props.initialFilters,
    temporarySettings: queryTmpSettingsOption,
    tableRowDetailContainer: contentAfterContainer,
  });

  const { loading: sharedViewLoading } = useTableSharedView(apiTableModel);

  return useMemo(
    () => ({
      apiTableModel,
      tableDeviceView,
      sharedViewLoading,
    }),
    [apiTableModel, tableDeviceView, sharedViewLoading],
  );
};
